// src/lib/helpers/groupSlots.ts
import type { ApiSlot, MpfSlotWeek } from '../../types/appointment';

/** ISO date (YYYY-MM-DD) of the Monday on or before `date`, in local time. */
function mondayOf(date: Date): string {
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));

  return toIsoDate(monday);
}

/** Local-time ISO date. `toISOString` would shift a late-evening slot to the next day. */
function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

// Rebuilds the weeks the API flattens; weeks begin on a Monday. Grouped by the
// patient's timezone, the same basis as the labels, so the two never disagree.
export function groupSlotsIntoWeeks(slots: ApiSlot[]): MpfSlotWeek[] {
  const weeks = new Map<string, Map<string, ApiSlot[]>>();

  for (const slot of slots) {
    const startsAt = new Date(slot.startDateTime);
    if (Number.isNaN(startsAt.getTime())) continue;

    const weekStart = mondayOf(startsAt);
    const day = toIsoDate(startsAt);

    const days = weeks.get(weekStart) ?? new Map<string, ApiSlot[]>();
    days.set(day, [...(days.get(day) ?? []), slot]);
    weeks.set(weekStart, days);
  }

  return [...weeks.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStartDate, days]) => ({
      weekStartDate,
      days: [...days.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, daySlots]) => ({
          date,
          slots: daySlots
            .slice()
            .sort((a, b) => a.startDateTime.localeCompare(b.startDateTime))
            .map(({ slotId, startDateTime }) => ({ slotId, startsAt: startDateTime })),
        })),
    }));
}
